/**
 * AttributeField — one schema-driven patient-attribute form control: a
 * `values`-constrained field renders as a <select>, everything else as
 * free text; a `required` field gets a marker; the label resolves via
 * `patients.attributes.<key>` (i18n) with a humanised fallback for a
 * config-introduced key that has no translation yet.
 *
 * Used by PatientAdminBoard's AttributesSection (in-place edit on the census
 * detail panel). PatientRegistrationForm no longer renders through it: that
 * form hands the served `$schema` document to `@rjsf/core`, which derives the
 * controls from the document itself (see `rjsfShadcn.tsx`). This control stays
 * because the census editor is a compact in-card form on a row the board
 * already holds, not a full document render — and both read the SAME
 * `$defs.create` shape, so the two cannot disagree about which fields exist.
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { humaniseAttributeKey } from '@/lib/patientLabel';
import type { PatientAttributeField } from '@/api/types';

// Both current callers land on the SAME 44px control height (registration's
// `min-h-[44px]` and the census editor's `min-h-11` are the same value —
// Tailwind's `11` spacing step is 2.75rem/44px), so it is not exposed as a
// prop; only label density genuinely differs between a full registration
// form and a compact in-card editor.
const SELECT_CLASSNAME =
  'flex min-h-11 w-full rounded-[8px] border border-input bg-background px-3 py-2 text-base ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:text-sm';
const INPUT_CLASSNAME = 'min-h-11';
const DEFAULT_LABEL_CLASSNAME = 'block text-[15px] font-medium text-neutral-900';
/** An unfilled required field, once the nurse has tried to submit. Never
 *  colour-alone: the border change rides along with the "Pflichtfeld" marker
 *  in the label and a named message above the submit button. */
const INVALID_CLASSNAME = 'border-destructive focus-visible:ring-destructive';

interface AttributeFieldProps {
  field: PatientAttributeField;
  value: string;
  onChange: (value: string) => void;
  /** Prefixes the control's `id`/`name` (e.g. "attr" vs "edit-attr") so two
   *  field loops rendered on the SAME page never collide on id. */
  idPrefix: string;
  /** Rendered below the control — the 422 violation that rejected this
   *  field's value. Omitted when there is nothing to show. */
  hint?: ReactNode;
  /** Registration is a full-page form (larger, `text-[15px]` labels); the
   *  census editor is a compact in-card form — override there. */
  labelClassName?: string;
  /** Marks this control as failing its own requirement (set after a submit
   *  attempt, never while typing — a field that goes red as you first tab into
   *  it is scolding, not helping). */
  invalid?: boolean;
}

export function AttributeField({
  field,
  value,
  onChange,
  idPrefix,
  hint,
  labelClassName = DEFAULT_LABEL_CLASSNAME,
  invalid = false,
}: AttributeFieldProps) {
  const { t } = useTranslation();
  const { key, required, values, pattern } = field;
  const id = `${idPrefix}-${key}`;
  // A bare red asterisk is a convention, not an explanation, and it is the
  // only thing that ever marked a required field here. Name the requirement in
  // words next to the label — the mirror of the `common.optional` marker the
  // registration form's identifier group already carries.
  const requiredMarker = required && (
    <span className="ml-1 font-normal text-[13px] text-neutral-500">
      {t('common.required')}
    </span>
  );

  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className={labelClassName}>
        {t(`patients.attributes.${key}`, humaniseAttributeKey(key))}
        {requiredMarker}
      </label>
      {values && values.length > 0 ? (
        <select
          id={id}
          name={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-required={required || undefined}
          aria-invalid={invalid || undefined}
          className={cn(SELECT_CLASSNAME, invalid && INVALID_CLASSNAME)}
        >
          <option value="" />
          {values.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>
      ) : (
        <Input
          id={id}
          name={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete="off"
          aria-required={required || undefined}
          aria-invalid={invalid || undefined}
          className={cn(INPUT_CLASSNAME, invalid && INVALID_CLASSNAME)}
          // Native constraint hint only — the server's anchored RE2 check is
          // the authority; this just lets the browser flag the shape early.
          pattern={pattern}
          title={pattern ? t('patients.attributes.pattern_hint', { pattern }) : undefined}
        />
      )}
      {pattern && (
        <p className="text-xs text-muted-foreground">
          {t('patients.attributes.pattern_hint', { pattern })}
        </p>
      )}
      {hint}
    </div>
  );
}
